import type { CreateNodeDraft, CreateNodeResponseDraft } from '../../entities/ai-assist-config';
import type { NodeType } from '../../entities/node';
import { createEmptyCreateNodeResponseDraft } from '../../entities/ai-assist-config';
import { isPlainObject, validateAINodeFieldObject } from './ai-node-field-validation';

export function validateCreateNodeResponse(rawText: string): CreateNodeResponseDraft {
  const draft = createEmptyCreateNodeResponseDraft();
  draft.rawText = rawText;

  if (!rawText.trim()) {
    return draft;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    return invalidDraft(rawText, 'invalid-json', ['Response must be valid JSON only.']);
  }

  const issues = validateCreateNodePayload(parsed);
  if (issues.length > 0) {
    return invalidDraft(rawText, 'invalid-contract', issues);
  }

  return {
    rawText,
    parsedPayload: parsed as CreateNodeDraft,
    validationStatus: 'valid',
    issues: [],
    validatedAt: new Date().toISOString(),
  };
}

function invalidDraft(
  rawText: string,
  validationStatus: CreateNodeResponseDraft['validationStatus'],
  issues: string[],
): CreateNodeResponseDraft {
  return {
    rawText,
    parsedPayload: null,
    validationStatus,
    issues,
    validatedAt: new Date().toISOString(),
  };
}

function validateCreateNodePayload(parsed: unknown): string[] {
  if (!isPlainObject(parsed)) {
    return ['Top-level response must be a JSON object.'];
  }

  const issues: string[] = [];
  const { nodeType, ...fields } = parsed;

  if (!isNodeType(nodeType)) {
    issues.push('nodeType is invalid.');
    return issues;
  }
  if ('nodeId' in parsed) {
    issues.push('nodeId must be omitted for create-node JSON.');
  }

  issues.push(...validateAINodeFieldObject(nodeType, fields, { requireDisplayName: true }));
  return issues;
}

function isNodeType(value: unknown): value is NodeType {
  return value === 'Human' || value === 'Project' || value === 'Occupation';
}